import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowLeft, Users, Mail, Phone, Building, Shield, Edit, Trash2, UserPlus, Search, Settings } from "lucide-react";
import PermissionsMatrix from "@/components/PermissionsMatrix";
import { extractErrorMessage } from "@/lib/errorMessage";

const roles = [
  { value: "admin", label: "Administrador", color: "bg-purple-100 text-purple-700" },
  { value: "engineering", label: "Engenharia", color: "bg-blue-100 text-blue-700" },
  { value: "operational", label: "Operacional", color: "bg-emerald-100 text-emerald-700" },
  { value: "client", label: "Cliente", color: "bg-amber-100 text-amber-700" }
];

function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [availableSources, setAvailableSources] = useState({ funcionarios: [], clientes_fornecedores: [] });
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceSearch, setSourceSearch] = useState("");
  const [newUserData, setNewUserData] = useState({ email: "", password: "", role: "operational", permissions: {} });
  const [creating, setCreating] = useState(false);
  const [systemModules, setSystemModules] = useState({ modules: {}, permission_types: [], default_permissions: {} });
  const [showPermissions, setShowPermissions] = useState(false);
  
  // Dialog para editar permissões de usuário existente
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [editPermissionsData, setEditPermissionsData] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchSystemModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole]);

  const fetchUsers = async () => {
    try {
      let url = `${API}/users`;
      if (filterRole && filterRole !== "all") url += `?role=${filterRole}`;
      const response = await axios.get(url);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemModules = async () => {
    try {
      const response = await axios.get(`${API}/system/modules`);
      setSystemModules(response.data);
    } catch (error) {
      console.log("Error fetching modules");
    }
  };

  const fetchAvailableSources = async () => {
    try {
      const response = await axios.get(`${API}/users/available-sources`);
      setAvailableSources(response.data);
    } catch (error) {
      toast.error("Erro ao carregar cadastros disponíveis");
    }
  };

  const handleOpenCreateDialog = () => {
    fetchAvailableSources();
    setShowCreateDialog(true);
    setSelectedSource(null);
    setSourceSearch("");
    setNewUserData({ email: "", password: "", role: "operational", permissions: {} });
    setShowPermissions(false);
  };

  const handleSelectSource = (type, source) => {
    setSelectedSource({ type, data: source });
    const defaultRole = type === "funcionario" ? "operational" : "client";
    const defaultPerms = systemModules.default_permissions[defaultRole] || {};
    setNewUserData({
      ...newUserData,
      email: source.email || "",
      role: defaultRole,
      permissions: { ...defaultPerms }
    });
  };

  const handleRoleChange = (role) => {
    const defaultPerms = systemModules.default_permissions[role] || {};
    setNewUserData({
      ...newUserData,
      role: role,
      permissions: { ...defaultPerms }
    });
  };

  const handleOpenEditPermissions = (user) => {
    setEditingPermissions(user);
    setEditPermissionsData(user.permissions || systemModules.default_permissions[user.role] || {});
  };

  const handleSavePermissions = async () => {
    if (!editingPermissions) return;
    setSavingPermissions(true);
    try {
      await axios.put(`${API}/users/${editingPermissions.id}/permissions`, {
        permissions: editPermissionsData
      });
      toast.success("Permissões atualizadas com sucesso!");
      setEditingPermissions(null);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao salvar permissões"));
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleCreateUser = async () => {
    if (!selectedSource) {
      toast.error("Selecione um cadastro");
      return;
    }
    if (!newUserData.email || !newUserData.password) {
      toast.error("Preencha email e senha");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/users/create-from-source`, {
        source_type: selectedSource.type === "funcionario" ? "funcionario" : "cliente_fornecedor",
        source_id: selectedSource.data.id,
        email: newUserData.email,
        password: newUserData.password,
        role: newUserData.role,
        permissions: newUserData.permissions
      });
      toast.success("Usuário criado com sucesso!");
      setShowCreateDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao criar usuário"));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Usuário excluído com sucesso");
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao excluir usuário"));
    }
  };

  const getRoleBadge = (role) => {
    const r = roles.find(rl => rl.value === role);
    return r ? <Badge className={r.color}>{r.label}</Badge> : <Badge>{role}</Badge>;
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U";
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const _q = sourceSearch.trim().toLowerCase();
  const filteredFuncionarios = _q
    ? availableSources.funcionarios.filter(f =>
        (f.nome || "").toLowerCase().includes(_q) ||
        (f.email || "").toLowerCase().includes(_q) ||
        (f.cargo_nome || "").toLowerCase().includes(_q)
      )
    : availableSources.funcionarios;
  const filteredClientes = _q
    ? availableSources.clientes_fornecedores.filter(c =>
        (c.nome || "").toLowerCase().includes(_q) ||
        (c.email || "").toLowerCase().includes(_q)
      )
    : availableSources.clientes_fornecedores;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Usuários do Sistema</h1>
        <Button 
          onClick={handleOpenCreateDialog}
          className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" 
          data-testid="new-user-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Habilitar Usuário
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-blue-50 border-l-4 border-l-blue-500">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Acesso Controlado</p>
              <p className="text-xs text-blue-600 mt-1">
                Novos usuários devem ser criados a partir de cadastros existentes (Funcionários LuvCorp ou Clientes/Fornecedores).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <NativeSelect
              value={filterRole || "all"}
              onValueChange={(v) => setFilterRole(v === "all" ? "" : v)}
              placeholder="Todos os tipos"
              options={[
                { value: "all", label: "Todos os tipos" },
                ...roles.map(r => ({ value: r.value, label: r.label }))
              ]}
              data-testid="filter-role"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuário</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Cadastro</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-luvcorp-navy text-white text-xs">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{user.email}</TableCell>
                  <TableCell className="text-sm text-slate-600">{user.phone || "-"}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleOpenEditPermissions(user)}
                        title="Editar Permissões"
                        className="text-blue-600 hover:text-blue-700"
                        data-testid={`edit-permissions-${user.id}`}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                      <Link to={`/users/${user.id}/edit`}>
                        <Button variant="ghost" size="sm" title="Editar Usuário">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" title="Excluir">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir o usuário "{user.name}"? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteUser(user.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredUsers.length === 0 && (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum usuário encontrado</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-luvcorp-navy">Habilitar Novo Usuário</DialogTitle>
            <DialogDescription>
              Selecione um cadastro existente para criar as credenciais de acesso
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="funcionarios" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="funcionarios">Funcionários LuvCorp</TabsTrigger>
              <TabsTrigger value="clientes">Clientes/Fornecedores</TabsTrigger>
            </TabsList>

            <div className="relative my-3">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                value={sourceSearch}
                onChange={(e) => setSourceSearch(e.target.value)}
                placeholder="Buscar por nome..."
                className="pl-9"
                autoFocus
                data-testid="source-search-input"
              />
            </div>

            <TabsContent value="funcionarios" className="space-y-4">
              {availableSources.funcionarios.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  Todos os funcionários já possuem acesso ao sistema
                </p>
              ) : filteredFuncionarios.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="funcionarios-empty-search">
                  Nenhum funcionário encontrado para &quot;{sourceSearch}&quot;
                </p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="funcionarios-list">
                  {filteredFuncionarios.map((func) => (
                    <div
                      key={func.id}
                      onClick={() => handleSelectSource("funcionario", func)}
                      data-testid={`source-funcionario-${func.id}`}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedSource?.data?.id === func.id
                          ? "border-luvcorp-gold bg-luvcorp-gold/5"
                          : "hover:border-slate-300"
                      }`}
                    >
                      <p className="font-medium">{func.nome}</p>
                      <p className="text-sm text-slate-500">{func.cargo_nome || "Sem cargo"} • {func.email || "Sem email"}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="clientes" className="space-y-4">
              {availableSources.clientes_fornecedores.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  Todos os clientes/fornecedores já possuem acesso ao sistema
                </p>
              ) : filteredClientes.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="clientes-empty-search">
                  Nenhum cliente/fornecedor encontrado para &quot;{sourceSearch}&quot;
                </p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="clientes-list">
                  {filteredClientes.map((cf) => (
                    <div
                      key={cf.id}
                      onClick={() => handleSelectSource("cliente_fornecedor", cf)}
                      data-testid={`source-cliente-${cf.id}`}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedSource?.data?.id === cf.id
                          ? "border-luvcorp-gold bg-luvcorp-gold/5"
                          : "hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{cf.nome}</p>
                        <Badge variant="outline" className="text-xs">
                          {cf.tipo_cadastro === "cliente" ? "Cliente" : "Fornecedor"}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">{cf.email || "Sem email"}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {selectedSource && (
            <div className="space-y-4 pt-4 border-t">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Cadastro selecionado:</p>
                <p className="font-medium text-luvcorp-navy">{selectedSource.data.nome}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="user-email">Email de Acesso *</Label>
                  <Input
                    id="user-email"
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                    placeholder="usuario@email.com"
                    data-testid="input-user-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-password">Senha *</Label>
                  <Input
                    id="user-password"
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                    placeholder="••••••••"
                    data-testid="input-user-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tipo de Acesso *</Label>
                <NativeSelect
                  value={newUserData.role}
                  onValueChange={(value) => setNewUserData({...newUserData, role: value})}
                  placeholder="Selecione o tipo"
                  options={roles.map(r => ({ value: r.value, label: r.label }))}
                  data-testid="select-role"
                />
              </div>

              {/* Permissões */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Permissões de Acesso</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowPermissions(!showPermissions)}
                  >
                    {showPermissions ? "Ocultar" : "Configurar Permissões"}
                  </Button>
                </div>
                
                {showPermissions && systemModules.modules && (
                  <div className="max-h-[400px] overflow-y-auto border rounded-lg p-3">
                    <PermissionsMatrix
                      modules={systemModules.modules}
                      permissionTypes={systemModules.permission_types}
                      permissions={newUserData.permissions}
                      onChange={(perms) => setNewUserData({...newUserData, permissions: perms})}
                    />
                  </div>
                )}
                
                {!showPermissions && (
                  <p className="text-sm text-slate-500">
                    As permissões padrão do perfil "{roles.find(r => r.value === newUserData.role)?.label}" serão aplicadas.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={!selectedSource || creating}
              className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
              data-testid="create-user-btn"
            >
              {creating ? "Criando..." : "Criar Usuário"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Editar Permissões */}
      <Dialog open={!!editingPermissions} onOpenChange={(open) => !open && setEditingPermissions(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-luvcorp-navy flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Editar Permissões - {editingPermissions?.name}
            </DialogTitle>
            <DialogDescription>
              Configure as permissões de acesso aos módulos do sistema
            </DialogDescription>
          </DialogHeader>

          {systemModules.modules && (
            <PermissionsMatrix
              modules={systemModules.modules}
              permissionTypes={systemModules.permission_types}
              permissions={editPermissionsData}
              onChange={setEditPermissionsData}
            />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPermissions(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSavePermissions}
              disabled={savingPermissions}
              className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
            >
              {savingPermissions ? "Salvando..." : "Salvar Permissões"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: ""
  });

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${id}`);
      setUser(response.data);
      setFormData({
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        role: response.data.role || ""
      });
    } catch (error) {
      toast.error("Erro ao carregar usuário");
      navigate("/users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const params = new URLSearchParams();
      if (formData.name) params.append("name", formData.name);
      if (formData.email) params.append("email", formData.email);
      if (formData.phone) params.append("phone", formData.phone);
      if (formData.role) params.append("role", formData.role);

      await axios.put(`${API}/users/${id}?${params.toString()}`);
      toast.success("Usuário atualizado com sucesso!");
      navigate("/users");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao atualizar usuário"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto" data-testid="edit-user">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/users">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Editar Usuário</h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="input-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="input-phone"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de Acesso</Label>
              <NativeSelect
                value={formData.role}
                onValueChange={(value) => setFormData({...formData, role: value})}
                options={roles.map(r => ({ value: r.value, label: r.label }))}
                data-testid="select-role"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white h-11"
              disabled={saving}
              data-testid="submit-btn"
            >
              {saving ? "Salvando..." : "Atualizar Usuário"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function UsersPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<UsersList />} />
        <Route path=":id/edit" element={<EditUser />} />
      </Routes>
    </MainLayout>
  );
}
